import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, CheckCircle, Mail } from 'lucide-react';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import './CustomerAuth.css';

const CustomerForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [error, setError] = useState('');

  const { forgotPassword } = useCustomerAuth();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setError('');
    setMessage({ type: '', text: '' });
  };

  const validateForm = () => {
    if (!email.trim()) {
      setError('Email is required');
      return false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      const result = await forgotPassword(email);
      if (result.success) {
        setMessage({ 
          type: 'success', 
          text: 'Password reset instructions have been sent to your email address.' 
        });
        setEmail('');
      } else {
        setMessage({ type: 'error', text: result.message });
      }
    } catch (error: any) {
      setMessage({ 
        type: 'error', 
        text: error.message || 'Failed to send reset instructions. Please try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="partner-login+">
      <div className="row m-0 align-items-center">
        <div className="col-md-5 p-0">
          <div className="left-login">
            <img className="left-log" src="/assets/images/login-bk.png" alt=""/>
          </div>
        </div>
        <div className="col-md-7 p-0">
          <div className="right-login">
            <div className="innerpart-form">
              <div className="customer-auth-card">
                <div className="auth-header">
                  <div className="auth-icon">
                    <Mail size={32} />
                  </div>
                  <h1>Forgot Password</h1>
                  <p>Enter your email to receive password reset instructions</p>
                </div>

                {message.text && (
                  <div className={`auth-message ${message.type}`}>
                    {message.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
                    <span>{message.text}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="auth-form">
                  <div className="form-group">
                    <span className='ic'><img src='/assets/images/sms.png' alt=""/></span>
                    <label htmlFor="email">Email Address</label>
                    <div className="input-wrapper">
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={email}
                        onChange={handleInputChange}
                        placeholder="Enter your email"
                        className={error ? 'error' : ''}
                      />
                    </div>
                    {error && <span className="error-text">{error}</span>}
                  </div>

                  <button
                    type="submit"
                    className="auth-button"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="loading-spinner small" />
                        Sending...
                      </>
                    ) : 'Send Reset Instructions'}
                  </button>
                </form>

                <div className="auth-footer">
                  <p>
                    Remember your password?{' '}
                    <Link to="/login" className="auth-link">
                      Sign in here
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CustomerForgotPassword; 